import React, { memo, useMemo } from "react";
import { Box } from "@mui/material";
import StringUtils from "@shared/utils/StringUtils";
import SelectToggleButton from "@shared/components/Select/SelectToggleButton";
import SelectedOptionsList from "@shared/components/SelectedOptionsList/SelectedOptionsList";
import { SelfServiceMultiselectProps, SelfServiceMultiselectOption } from "@myProfile/models";
import SelfServiceList from "./SelfServiceList";
import SelfServiceDropdown from "./SelfServiceDropdown";

const SelfServiceMultiselect = ({
  loading,
  entity,
  allLabel,
  onSelect,
  onClearAll,
  disabled,
  testId,
  variant,
  previewExpandable,
  dropdownWidth,
  onToggleButtonClick,
  constrainToDialog,
  options,
  size,
  withSearch,
}: SelfServiceMultiselectProps) => {
  const selectedOptions = useMemo(() => {
    return options.filter((option) => option.selected);
  }, [options]);

  let toggleButtonText = allLabel || `All ${entity}s`;

  if (selectedOptions.length) {
    toggleButtonText = `${selectedOptions.length} ${StringUtils.pluralize(selectedOptions.length, entity)} selected`;
  }

  return (
    <Box data-testid={testId ? `multiselect-container-${testId}` : "multiselect-container"}>
      {!!selectedOptions.length && (
        <SelectedOptionsList
          selectedOptions={selectedOptions}
          previewExpandable={previewExpandable}
          entity={entity}
          onClearAll={onClearAll}
          optionsLength={options.length}
          onSelect={onSelect}
          testId={testId}
        />
      )}
      <SelfServiceDropdown
        width={dropdownWidth}
        constrainToDialog={constrainToDialog}
        renderToggle={({ ref, toggleDropdown, open }) => (
          <SelectToggleButton
            open={open}
            ref={ref}
            onClick={() => {
              toggleDropdown();

              if (onToggleButtonClick) onToggleButtonClick();
            }}
            variant={variant}
            loading={loading}
            disabled={disabled}
            size={size}
            testId={testId}
          >
            {toggleButtonText}
          </SelectToggleButton>
        )}
      >
        <SelfServiceList<SelfServiceMultiselectOption>
          options={options}
          size={size}
          withSearch={withSearch}
          variant="multiselect"
          selectedOptionIds={selectedOptions.map((option) => option.id)}
          onOptionClick={(option) => onSelect({ ...option, selected: !option.selected })}
        />
      </SelfServiceDropdown>
    </Box>
  );
};

export default memo(SelfServiceMultiselect);
